import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate, useOutletContext } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import CityHubTabHeader from "../CityHubTabHeader"
import {
  CityHubInfiniteSentinel,
  ReviewCard,
  ReviewCardSkeleton,
} from "../CityHubCards"
import {
  AddReviewModal,
  BecomeReviewWriterModal,
  PhotographyPreviewModal,
} from "../CityHubModals"
import { CITY_HUB_INFINITE_PAGE_SIZE, PUBLIC_CITY_INFINITE_PAGE_SIZE, unwrapCityHubPagedResult } from "../cityHubTabs"
import EmptyState from "../../../components/Common/EmptyState"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import useInfinitePagination from "../../../hooks/useInfinitePagination"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import {
  REVIEW_WRITER_CTA,
  fetchCityReviewWriterRequest,
  getReviewWriterCtaLabel,
  isAcceptedReviewWriterRequest,
} from "../cityReviewWriterRequest"
import { canManageReview, fetchCityReviewDetail, fetchPublicCityReviewDetail, mapApiReview } from "../cityReviews"
import { goToRequestInvite } from "../../PublicCity/publicCityPaths"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const SKELETON_COUNT = 4
const LOAD_MORE_SKELETON_COUNT = 2

const AUTHOR_FILTER_OPTIONS = [{ value: "mine", label: "My reviews" }]

const normalizeSingleFilter = next =>
  Array.isArray(next) ? next[0] || "" : next || ""

const mergeReviewPages = (prev, next) => {
  const seen = new Set(prev.map(item => String(item.id)))
  return [
    ...prev,
    ...next.filter(item => item?.id && !seen.has(String(item.id))),
  ]
}

const CityHubReviewsTab = () => {
  const navigate = useNavigate()
  const {
    tab,
    city,
    isCityLoading = false,
    isCityMember = false,
    isPublic = false,
    listScrollRootRef,
  } = useOutletContext()
  const cityId = city?.id || null
  const currentUser = useSelector(state => state.auth.currentUser)
  const currentUserId = currentUser?.id || null

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [authorFilter, setAuthorFilter] = useState("")
  const [reviews, setReviews] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState(null)
  const sentinelRef = useRef(null)
  const pageRef = useRef(1)
  const loadInFlightRef = useRef(false)

  const [writerRequest, setWriterRequest] = useState(null)
  const [isRequestLoading, setIsRequestLoading] = useState(true)

  const [isBecomeOpen, setIsBecomeOpen] = useState(false)
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [editingReviewId, setEditingReviewId] = useState(null)
  const [previewReviewId, setPreviewReviewId] = useState(null)
  const [deletingReview, setDeletingReview] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const ctaLabel = getReviewWriterCtaLabel(writerRequest)
  const canWrite = isAcceptedReviewWriterRequest(writerRequest)
  const ctaBusy = isCityLoading || isRequestLoading
  const canContribute = Boolean(cityId && isCityMember && !isPublic)

  const openInvite = useCallback(() => {
    goToRequestInvite(
      navigate,
      { from: "public-city", cityId, tab: "reviews", city },
      { isAuthenticated: Boolean(currentUserId) },
    )
  }, [city, cityId, currentUserId, navigate])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const loadWriterRequest = useCallback(
    async ({ signal } = {}) => {
      if (!cityId || isPublic) {
        setWriterRequest(null)
        setIsRequestLoading(false)
        return
      }

      setIsRequestLoading(true)
      const result = await fetchCityReviewWriterRequest({
        cityId,
        userId: currentUserId,
        signal,
      })

      if (signal?.aborted || result?.aborted) return

      if (result?.ok) {
        setWriterRequest(result.request)
      }
      setIsRequestLoading(false)
    },
    [cityId, currentUserId, isPublic],
  )

  useEffect(() => {
    const controller = new AbortController()
    setWriterRequest(null)
    loadWriterRequest({ signal: controller.signal })
    return () => controller.abort()
  }, [loadWriterRequest])

  const fetchReviews = useCallback(
    async ({ page: pageToLoad = 1, append = false } = {}) => {
      if (!cityId) {
        setReviews([])
        setHasMore(false)
        setError(null)
        setIsLoading(false)
        setIsLoadingMore(false)
        return
      }

      if (append) setIsLoadingMore(true)
      else {
        setIsLoading(true)
        setError(null)
      }

      try {
        const pageSize = isPublic
          ? PUBLIC_CITY_INFINITE_PAGE_SIZE
          : CITY_HUB_INFINITE_PAGE_SIZE
        const params = {
          page: pageToLoad,
          per_page: pageSize,
          page_size: pageSize,
          search: debouncedSearch || "",
        }
        if (authorFilter === "mine" && currentUserId) {
          params.user_id = currentUserId
        }

        const response = await get(
          isPublic
            ? endpoints.getPublicCityReviews(cityId)
            : endpoints.getCityReviews(cityId),
          { params },
        )

        if (!response?.data?.success) {
          const message = toastApiError(response, "Failed to load reviews")
          setError(message)
          if (!append) setReviews([])
          setHasMore(false)
          return
        }

        const { list, currentPage, hasMore: nextHasMore } =
          unwrapCityHubPagedResult(response, {
            pageToLoad,
            pageSize,
          })
        const mapped = list.map(mapApiReview)

        setReviews(prev => (append ? mergeReviewPages(prev, mapped) : mapped))
        pageRef.current = currentPage
        setHasMore(nextHasMore)
      } catch (err) {
        const message = toastApiError(err, "Failed to load reviews")
        setError(message)
        if (!append) setReviews([])
        setHasMore(false)
      } finally {
        loadInFlightRef.current = false
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [cityId, currentUserId, debouncedSearch, authorFilter, isPublic],
  )

  useEffect(() => {
    pageRef.current = 1
    fetchReviews({ page: 1, append: false })
  }, [fetchReviews])

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoading ||
      isLoadingMore ||
      loadInFlightRef.current
    ) {
      return
    }
    loadInFlightRef.current = true
    fetchReviews({ page: pageRef.current + 1, append: true })
  }, [fetchReviews, hasMore, isLoading, isLoadingMore])

  useInfinitePagination({
    hasMore: hasMore && !isLoading,
    isLoading: isLoadingMore,
    onLoadMore: loadMore,
    resetKey: reviews.length,
    sentinelRef,
    rootRef: listScrollRootRef,
  })

  const filterConfig = useMemo(
    () => [
      {
        key: "user_id",
        type: "select",
        label: "Author",
        multiple: false,
        options: AUTHOR_FILTER_OPTIONS,
        value: authorFilter,
        onChange: next => {
          setAuthorFilter(normalizeSingleFilter(next))
        },
      },
    ],
    [authorFilter],
  )

  const handleCtaClick = () => {
    if (!cityId || ctaBusy || !canContribute) return
    if (canWrite) {
      setEditingReviewId(null)
      setIsReviewOpen(true)
      return
    }
    setIsBecomeOpen(true)
  }

  const handleEditReview = review => {
    if (!review?.id) return
    setPreviewReviewId(null)
    setEditingReviewId(review.id)
    setIsReviewOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingReview?.id) return

    const resolvedCityId = cityId || deletingReview.cityId
    if (!resolvedCityId) {
      toast.error("City is required to delete this review.", TOAST_OPTIONS)
      return
    }

    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteReview(deletingReview.id), {
        params: { city_id: resolvedCityId },
      })
      if (!response?.data?.success) {
        toastApiError(response, "Failed to delete review")
        return
      }
      toast.success(response.data.message || "Review deleted", TOAST_OPTIONS)
      setDeletingReview(null)
      setReviews(prev =>
        prev.filter(item => String(item.id) !== String(deletingReview.id)),
      )
      await fetchReviews({ page: 1, append: false })
    } catch (err) {
      toastApiError(err, "Failed to delete review")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRequestSaved = savedRequest => {
    if (savedRequest?.id) setWriterRequest(savedRequest)
    loadWriterRequest()
  }

  const handleCloseReviewModal = useCallback(() => {
    setIsReviewOpen(false)
    setEditingReviewId(null)
  }, [])

  const handleReviewSaved = useCallback(() => {
    fetchReviews({ page: 1, append: false })
  }, [fetchReviews])

  const fetchReviewDetailForPreview = useCallback(
    async (id, options = {}) => {
      if (isPublic) {
        const fromList = reviews.find(item => String(item.id) === String(id))
        if (fromList?.raw?.id) {
          return {
            ok: true,
            item: {
              ...fromList.raw,
              uploader: fromList.raw?.user || fromList.raw?.uploader || null,
              created_at:
                fromList.raw?.published_at || fromList.raw?.created_at || null,
            },
          }
        }
        const result = await fetchPublicCityReviewDetail(cityId, id, options)
        if (result?.aborted) return result
        if (!result?.ok) {
          return { ok: false, aborted: Boolean(result?.aborted) }
        }
        return result
      }

      const result = await fetchCityReviewDetail(cityId, id, options)
      if (result?.aborted) return result
      if (!result?.ok) {
        toastApiError(result?.response, "Failed to load review")
        return { ok: false, aborted: Boolean(result?.aborted) }
      }
      return result
    },
    [cityId, isPublic, reviews],
  )

  const showInitialSkeleton = isCityLoading || isLoading

  return (
    <>
      <CityHubTabHeader
        title={tab.pageTitle}
        subtitle={tab.pageSubtitle}
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        searchPlaceholder={tab.searchPlaceholder}
        filterConfig={filterConfig}
        showFilter={tab.showFilter && !isPublic}
        ctaLabel={canContribute ? ctaLabel : null}
        ctaHideIcon={ctaLabel === REVIEW_WRITER_CTA.edit}
        ctaLoading={ctaBusy}
        ctaDisabled={!canContribute || ctaBusy}
        onCtaClick={handleCtaClick}
      />

      <div
        className="city-hub__reviews-grid"
        aria-busy={showInitialSkeleton || isLoadingMore || undefined}
      >
        {showInitialSkeleton
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <ReviewCardSkeleton key={`city-review-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton &&
          reviews.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              canManage={!isPublic && canManageReview(review, currentUser)}
              onOpen={item => {
                if (isPublic) {
                  openInvite()
                  return
                }
                setPreviewReviewId(item.id)
              }}
              onEdit={isPublic ? undefined : handleEditReview}
              onDelete={isPublic ? undefined : setDeletingReview}
            />
          ))}

        {!showInitialSkeleton && isLoadingMore
          ? Array.from({ length: LOAD_MORE_SKELETON_COUNT }).map((_, index) => (
              <ReviewCardSkeleton key={`city-review-more-skeleton-${index}`} />
            ))
          : null}

        {!showInitialSkeleton && !isLoadingMore && reviews.length === 0 ? (
          <EmptyState
            title={error ? "Unable to load reviews" : "No reviews found"}
            description={
              error ||
              (!cityId
                ? "Select a city in the banner to browse local reviews."
                : isPublic
                  ? "Nothing matches this search or filter yet."
                  : !isCityMember
                    ? "Only members of this city can write reviews. You can still browse as a visitor."
                    : "Nothing matches this search or filter yet.")
            }
            actionText={
              canContribute && !ctaBusy ? ctaLabel : undefined
            }
            onActionClick={
              canContribute && !ctaBusy ? handleCtaClick : undefined
            }
          />
        ) : null}
      </div>
      <CityHubInfiniteSentinel
        sentinelRef={sentinelRef}
        hasMore={hasMore && !isLoading}
      />

      {isPublic ? (
        <PhotographyPreviewModal
          isOpen={Boolean(previewReviewId)}
          itemId={previewReviewId}
          onClose={() => setPreviewReviewId(null)}
          fetchDetail={fetchReviewDetailForPreview}
          previewLabel="Review"
          emptyDescription="This review does not include any images yet."
        />
      ) : (
        <>
          <BecomeReviewWriterModal
            isOpen={isBecomeOpen && canContribute}
            onClose={() => setIsBecomeOpen(false)}
            cityId={cityId}
            request={writerRequest}
            onSuccess={handleRequestSaved}
          />

          <AddReviewModal
            isOpen={isReviewOpen && canContribute}
            onClose={handleCloseReviewModal}
            cityId={cityId}
            reviewId={editingReviewId}
            onSuccess={handleReviewSaved}
          />

          <PhotographyPreviewModal
            isOpen={Boolean(previewReviewId)}
            itemId={previewReviewId}
            onClose={() => setPreviewReviewId(null)}
            fetchDetail={fetchReviewDetailForPreview}
            previewLabel="Review"
            emptyDescription="This review does not include any images yet."
          />

          <DeleteConfirmation
            isOpen={Boolean(deletingReview)}
            onCancel={() => !isDeleting && setDeletingReview(null)}
            onConfirm={handleDeleteConfirm}
            title="Delete review"
            message="Are you sure you want to delete this review? This action cannot be undone."
            cancelText="Cancel"
            confirmText="Delete"
            isDeleting={isDeleting}
          />
        </>
      )}
    </>
  )
}

export default CityHubReviewsTab
